import { z } from 'zod/v4';

// Read by useHomePageBanners in the provider portal (client/src/hooks/useHomePageBanners.ts).
export const PROVIDER_ANNOUNCEMENT_BANNER_CONFIG_ID = 'rd_portal_announcement_banner';
export const PROVIDER_ANNOUNCEMENT_BANNER_STATSIG_URL =
  'https://console.statsig.com/6RFekk7eSu2Gu59L5p04xx/dynamic_configs/rd_portal_announcement_banner';

// When a rule splits traffic across variants, Dev Console writes into this variant and moves it to 100%
// so the hand-maintained variants in Statsig stay intact.
export const PROVIDER_ANNOUNCEMENT_BANNER_DEV_CONSOLE_VARIANT_NAME = 'Dev Console';

// Must stay a subset of ColorStyles in client/src/components/custom/Banner.tsx.
export const PROVIDER_ANNOUNCEMENT_BANNER_COLORS = ['error', 'warning', 'info', 'neutral', 'positive', 'ai'] as const;
export type ProviderAnnouncementBannerColor = (typeof PROVIDER_ANNOUNCEMENT_BANNER_COLORS)[number];

// Must stay a subset of CustomIconNames in client/src/components/custom/CustomIcon.tsx.
export const PROVIDER_ANNOUNCEMENT_BANNER_ICONS = [
  'information-circle-outline',
  'alert-circle-outline',
  'warning-outline',
] as const;
export type ProviderAnnouncementBannerIcon = (typeof PROVIDER_ANNOUNCEMENT_BANNER_ICONS)[number];

export const PROVIDER_ANNOUNCEMENT_BANNER_MAX_TITLE_LENGTH = 120;
export const PROVIDER_ANNOUNCEMENT_BANNER_MAX_TEXT_LENGTH = 500;

export const providerAnnouncementBannerSchema = z.object({
  title: z.string().trim().max(PROVIDER_ANNOUNCEMENT_BANNER_MAX_TITLE_LENGTH).optional(),
  text: z.string().trim().min(1, 'Banner text is required').max(PROVIDER_ANNOUNCEMENT_BANNER_MAX_TEXT_LENGTH),
  color: z.enum(PROVIDER_ANNOUNCEMENT_BANNER_COLORS),
  icon: z.enum(PROVIDER_ANNOUNCEMENT_BANNER_ICONS),
});

export type ProviderAnnouncementBanner = z.infer<typeof providerAnnouncementBannerSchema>;

export const providerAnnouncementBannerUpdateRequestSchema = z.object({
  ruleId: z.string().trim().min(1, 'ruleId is required'),
  // null turns the banner off for the rule.
  banner: providerAnnouncementBannerSchema.nullable(),
});

export type ProviderAnnouncementBannerUpdateRequest = z.infer<typeof providerAnnouncementBannerUpdateRequestSchema>;

export interface ProviderAnnouncementBannerPreset {
  label: string;
  banner: ProviderAnnouncementBanner;
}

export const PROVIDER_ANNOUNCEMENT_BANNER_PRESETS: readonly ProviderAnnouncementBannerPreset[] = [
  {
    label: 'Scheduling issue',
    banner: {
      text: "We're currently experiencing issues with scheduling and your calendar. Your existing appointments are not affected.",
      color: 'error',
      icon: 'information-circle-outline',
    },
  },
  {
    label: 'Charting issue',
    banner: {
      text: "We're currently experiencing issues with chart notes. Please save your work often and try again later.",
      color: 'error',
      icon: 'information-circle-outline',
    },
  },
  {
    label: 'AI notes outage',
    banner: {
      text: "We're currently experiencing issues with AI-generated notes and summaries. Please write notes manually until we have a fix.",
      color: 'error',
      icon: 'information-circle-outline',
    },
  },
  {
    label: 'Messaging issue',
    banner: {
      text: "We're currently experiencing issues with patient messaging. Messages may be delayed.",
      color: 'error',
      icon: 'information-circle-outline',
    },
  },
  {
    label: 'Latency issue',
    banner: {
      text: "We're currently experiencing slowness across the provider portal. Pages may take longer than usual to load.",
      color: 'error',
      icon: 'information-circle-outline',
    },
  },
  {
    label: 'General outage',
    banner: {
      text: "We're currently experiencing some issues and are working on a fix. Some features may be unavailable.",
      color: 'error',
      icon: 'information-circle-outline',
    },
  },
];

export interface ProviderAnnouncementBannerVariant {
  id: string | null;
  name: string;
  passPercentage: number;
  value: Record<string, unknown> | null;
}

export interface ProviderAnnouncementBannerRule {
  id: string;
  name: string;
  passPercentage: number;
  environments: string[] | null;
  // The rule's return value when it does not split into variants.
  value: Record<string, unknown> | null;
  variants: ProviderAnnouncementBannerVariant[];
}

export interface ProviderAnnouncementBannerConfigResponse {
  configId: string;
  statsigUrl: string;
  isEnabled: boolean;
  defaultValue: Record<string, unknown> | null;
  rules: ProviderAnnouncementBannerRule[];
  lastModifiedTime: number | null;
  lastModifierName: string | null;
}

export interface ProviderAnnouncementBannerUpdateResponse {
  // null when Statsig applied the update but re-reading the config failed; the client should refetch.
  config: ProviderAnnouncementBannerConfigResponse | null;
}

/**
 * Statsig suffixes rule ids with the pass percentage and an edit counter (`abc:100.00:12`), so the id changes on
 * every save. The part before the first colon stays the same.
 */
export function getProviderAnnouncementBannerRuleBaseId(ruleId: string): string {
  return ruleId.split(':')[0] ?? ruleId;
}

/**
 * The value a fully-matched user receives from a rule: the return value for a plain rule, or the
 * variant at 100% for a split rule. Returns `undefined` when traffic is split across variants.
 */
export function getProviderAnnouncementBannerRuleValue(
  rule: ProviderAnnouncementBannerRule
): Record<string, unknown> | null | undefined {
  if (rule.variants.length === 0) {
    return rule.value;
  }

  const activeVariant = rule.variants.find((variant) => variant.passPercentage === 100);
  return activeVariant == null ? undefined : activeVariant.value;
}

export function isProviderAnnouncementBannerValueVisible(value: Record<string, unknown> | null | undefined): boolean {
  return value != null && Object.keys(value).length > 0;
}
